import * as React from 'react';
import { backendBaseUrl } from '../config';

/**
 * WI-SSO-UI — the app-shell's single source of the caller's identity, read from the persisted
 * backend surface GET /api/me (persist-then-render). It is published by the ONE SSO session
 * exchange in AppAuthGate — which already fetches /api/me (previously fire-and-discarded) — so
 * the header never RACES the sso_session mint: React runs child effects before parent effects,
 * so a header-owned fetch would resolve BEFORE the exchange's POST /api/auth/session completed
 * and would misreport a freshly-logged-in admin as "Sign in" until a manual refresh. Components
 * only SUBSCRIBE (useIdentity); they never fetch.
 */
export interface Identity {
  /** True while the persisted identity read is in flight. */
  loading: boolean;
  /** The identity could not be read; this is never a verified anonymous session. */
  unavailable: boolean;
  /**
   * Signed in — a verified SSO session, read straight from `/api/me.authenticated`.
   *
   * ⚠️ AMENDED 2026-08-29 — operator ruling R-Q15 (FULL REMOVAL of the magic-link plane). This read
   * "Signed in on EITHER plane: a verified SSO oid (/api/me.authenticated) OR a magic-link customer",
   * and the value was literally ORed with a client-side `isCustomerSession()` localStorage probe in
   * `refreshIdentity` below. That second plane is FULL-DELETED, so the OR is gone and this field is
   * now purely a read of the PERSISTED backend answer — which is what persist-then-render wanted all
   * along. The consequence worth stating: no client-side value can make this true any more.
   */
  authenticated: boolean;
  /** Verified SSO admin (never trust for security — backend enforces authZ; this only drives UI). */
  isAdmin: boolean;
  /**
   * ⚠️ ANY FIELD ADDED HERE MUST HAVE A MATCHING WIRE FIELD READ IN `refreshIdentity` BELOW, AND ANY
   * FIELD ADDED TO `/api/me` MUST APPEAR HERE. A field published by the endpoint and absent from this
   * type is not a compile error — it is silently invisible, forever. That is asserted by
   * `apps/test-scripts/frontend/api/meIdentityContract.test.ts`, which derives BOTH field sets from
   * source and fails on any asymmetry.
   */
  /** Deployment capability; absent on older APIs means disabled. Backend still enforces it. */
  speechEnabled: boolean;
  /** Display-only first name from the verified SSO assertion; null for customers / no-name principals. */
  firstName: string | null;
  /**
   * WI-10a — the caller's OWN verified Entra object id (`/api/me.oid`), or null when there isn't one.
   *
   * It is the identity anchor the model registry's per-caller role is keyed on: a `model_access` list
   * is a list of oids, and this is the only way to answer "which of these rows is me". The ROLE itself
   * is NOT here — a role is a fact about a caller AND a resource, so it rides on that resource's
   * payload (`GET /api/models/{modelId}` → `caller.role`), not on this endpoint.
   *
   * ⚠️ NULL STILL DOES NOT MEAN SIGNED OUT, AND THE REASON CHANGED ON 2026-08-29 (ruling R-Q15).
   * The original reason was that a redeemed magic-link customer was `authenticated: true` with no
   * Entra principal at all, so `oid` was null for them. That plane is FULL-DELETED, so THAT case can
   * no longer arise. The rule survives on a narrower but still-live basis: this is `/api/me.oid`
   * narrowed to a non-empty string, so ANY session whose assertion carries no usable oid — an older
   * or partial principal, a claim the endpoint could not resolve — publishes null while
   * `authenticated` is true. `authenticated` remains the signed-in signal; never substitute this
   * for it, and never read null as "signed out".
   *
   * ⚠️ UI ONLY, like every other field here. The backend never reads an oid a client sends; it reads
   * the verified session. Do not send this back as an authorization argument.
   */
  oid: string | null;
}

const EMPTY_IDENTITY: Identity = { loading: false, unavailable: false, authenticated: false, isAdmin: false, speechEnabled: false, firstName: null, oid: null };

let current: Identity = { ...EMPTY_IDENTITY, loading: true };
const listeners = new Set<() => void>();
let latestRead = 0;

function publish(next: Identity): void {
  current = next;
  listeners.forEach((fn) => fn());
}

/**
 * Fetch GET /api/me and publish the identity. `fetchImpl` is the PRISTINE (un-intercepted) fetch
 * the exchange captures at module load — /api/me returns 200 for anonymous so it never trips the
 * 401 interceptor, but we stay on the pristine handle for parity with the exchange.
 *
 * Transport, timeout and invalid identity responses publish an explicit unavailable state. Only
 * a successful response containing a boolean authenticated field can establish anonymous identity.
 * The bound covers headers AND body consumption so a stalled JSON read also leaves the splash.
 */
export async function refreshIdentity(fetchImpl: typeof fetch): Promise<void> {
  const readId = ++latestRead;
  publish({ ...EMPTY_IDENTITY, loading: true });
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const read = async () => {
      const resp = await fetchImpl(`${backendBaseUrl}/api/me`, { credentials: 'include', signal: controller.signal });
      if (!resp.ok) throw new Error(`GET /api/me returned HTTP ${resp.status}`);
      const body = await resp.json() as {
        authenticated?: unknown;
        oid?: unknown;
        is_admin?: unknown;
        speech_enabled?: unknown;
        first_name?: unknown;
      };
      if (body === null || typeof body !== 'object' || typeof body.authenticated !== 'boolean') {
        throw new Error('GET /api/me returned an invalid authenticated field');
      }
      return body;
    };
    const timeout = new Promise<never>((_resolve, reject) => {
      timer = setTimeout(() => {
        reject(new Error('GET /api/me timed out'));
        controller.abort();
      }, 8000);
    });
    const body = await Promise.race([read(), timeout]);
    // An earlier overlapping read cannot restore identity after a newer reconcile failed.
    if (readId !== latestRead) return;
    const firstName =
      typeof body.first_name === 'string' && body.first_name.length > 0 ? body.first_name : null;
    const oid = typeof body.oid === 'string' && body.oid.length > 0 ? body.oid : null;
    publish({
      loading: false,
      unavailable: false,
      authenticated: body.authenticated === true,
      isAdmin: body.is_admin === true,
      // Optional capabilities are fail-closed on absent or non-boolean grants from older APIs.
      speechEnabled: body.speech_enabled === true,
      firstName,
      oid,
    });
  } catch (err) {
    console.warn('[sessionIdentity] GET /api/me unavailable:', err);
    if (readId === latestRead) publish({ ...EMPTY_IDENTITY, unavailable: true });
  } finally {
    clearTimeout(timer);
  }
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Non-reactive read of the current identity — for non-React callers and tests. */
export function getIdentity(): Identity {
  return current;
}

/** Subscribe a component to the published identity. The exchange (AppAuthGate) drives refresh. */
export function useIdentity(): Identity {
  return React.useSyncExternalStore(subscribe, getIdentity);
}
